// apps/web/src/hooks/useKnowledgeBase.ts
// TanStack Query hooks for Knowledge Base

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  api,
  type DocumentType,
  type KnowledgeBaseDocument,
  type KnowledgeBaseDocumentWithChunks,
  type KnowledgeBaseSearchResult,
  type KnowledgeBaseStats,
  type KnowledgeBaseCreateInput,
  type KnowledgeBaseUpdateInput,
} from '@/lib/api-client'

// Re-export types for convenience
export type {
  DocumentType,
  KnowledgeBaseDocument as Document,
  KnowledgeBaseDocumentWithChunks as DocumentWithChunks,
  KnowledgeBaseSearchResult as SearchResult,
  KnowledgeBaseStats,
  KnowledgeBaseCreateInput as CreateDocumentInput,
  KnowledgeBaseUpdateInput as UpdateDocumentInput,
}

// Query keys
export const knowledgeBaseKeys = {
  all: ['knowledge-base'] as const,
  lists: () => [...knowledgeBaseKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...knowledgeBaseKeys.lists(), filters] as const,
  details: () => [...knowledgeBaseKeys.all, 'detail'] as const,
  detail: (id: number) => [...knowledgeBaseKeys.details(), id] as const,
  stats: () => [...knowledgeBaseKeys.all, 'stats'] as const,
  search: (query: string) => [...knowledgeBaseKeys.all, 'search', query] as const,
}

// Hooks

/**
 * Get knowledge base statistics
 */
export function useKnowledgeBaseStats() {
  return useQuery({
    queryKey: knowledgeBaseKeys.stats(),
    queryFn: async () => {
      const res = await api.knowledgeBase.stats()
      return res.data
    },
  })
}

/**
 * List documents with pagination and filters
 */
export function useDocuments(filters: {
  page?: number
  limit?: number
  search?: string
  docType?: DocumentType
  branchId?: number
  isActive?: boolean
} = {}) {
  return useQuery({
    queryKey: knowledgeBaseKeys.list(filters),
    queryFn: async () => {
      const res = await api.knowledgeBase.list(filters)
      return {
        data: res.data,
        pagination: res.pagination,
      }
    },
  })
}

/**
 * Get single document with chunks
 */
export function useDocument(id: number) {
  return useQuery({
    queryKey: knowledgeBaseKeys.detail(id),
    queryFn: async () => {
      const res = await api.knowledgeBase.get(id)
      return res.data
    },
    enabled: id > 0,
  })
}

/**
 * Create new document
 */
export function useCreateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: KnowledgeBaseCreateInput) => {
      const res = await api.knowledgeBase.create(input)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.all })
    },
  })
}

/**
 * Update document
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: KnowledgeBaseUpdateInput & { id: number }) => {
      const res = await api.knowledgeBase.update(id, input)
      return res.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.all })
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.detail(variables.id) })
    },
  })
}

/**
 * Delete document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.knowledgeBase.delete(id)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.all })
    },
  })
}

/**
 * Semantic search
 */
export function useKnowledgeBaseSearch(query: string, options?: {
  limit?: number
  branchId?: number
  docType?: DocumentType
  threshold?: number
  enabled?: boolean
}) {
  return useQuery({
    queryKey: knowledgeBaseKeys.search(query),
    queryFn: async () => {
      const res = await api.knowledgeBase.search({
        query,
        limit: options?.limit,
        branchId: options?.branchId,
        docType: options?.docType,
        threshold: options?.threshold,
      })
      return {
        query: res.query,
        data: res.data,
      }
    },
    enabled: (options?.enabled ?? true) && query.length > 0,
  })
}

/**
 * Get RAG context for a query
 */
export function useRAGContext() {
  return useMutation({
    mutationFn: async (input: {
      query: string
      maxTokens?: number
      branchId?: number
    }) => {
      const res = await api.knowledgeBase.context(input)
      return {
        context: res.context,
        chunks: res.chunks,
        totalTokens: res.totalTokens,
      }
    },
  })
}
